import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ReportWithProfiles {
  report_id: string;
  reason: string;
  description: string | null;
  status: string;
  created_at: string;
  reporter_id: string;
  reporter_name: string;
  reporter_photos: string[];
  reported_id: string;
  reported_name: string;
  reported_photos: string[];
  reported_bio: string | null;
  reported_gender: string | null;
  reported_age: number | null;
}

export type ModerationAction = 'dismiss' | 'warn' | 'remove_photos' | 'ban';

export const useAdminReports = (status?: string) => {
  return useQuery({
    queryKey: ['admin-reports', status],
    queryFn: async (): Promise<ReportWithProfiles[]> => {
      const { data, error } = await supabase.rpc('get_reports_with_profiles', {
        p_status: status || null,
        p_limit: 100,
      });

      if (error) throw error;
      return (data as ReportWithProfiles[]) || [];
    },
    staleTime: 15_000,       // 15s — admin data refreshes more frequently
    gcTime: 5 * 60 * 1000,   // 5 min cache
    refetchInterval: 30000,
  });
};

export const useModerateReport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reportId,
      action,
      notes,
    }: {
      reportId: string;
      action: ModerationAction;
      notes?: string;
    }) => {
      const { data, error } = await supabase.rpc('moderate_report', {
        p_report_id: reportId,
        p_action: action,
        p_notes: notes || null,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-reports'] });
      
      const actionMessages: Record<ModerationAction, string> = {
        dismiss: 'Report dismissed',
        warn: 'Warning issued to user',
        remove_photos: 'Photos removed from profile',
        ban: 'User banned',
      };
      
      toast.success(actionMessages[variables.action]);
    },
    onError: (error) => {
      void error;
      toast.error('Failed to moderate report. Please try again.');
    },
  });
};

export const useIsAdmin = () => {
  return useQuery({
    queryKey: ['is-admin'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('is_admin');
      if (error) return false;
      return data as boolean;
    },
    staleTime: 10 * 60 * 1000, // 10 min — role rarely changes
    gcTime: 30 * 60 * 1000,    // 30 min cache
  });
};

export const useIsModerator = () => {
  return useQuery({
    queryKey: ['is-moderator'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('is_moderator');
      if (error) return false;
      return data as boolean;
    },
    staleTime: 10 * 60 * 1000, // 10 min — role rarely changes
    gcTime: 30 * 60 * 1000,    // 30 min cache
  });
};
